import { useEffect, useRef, useState } from 'react';
import { Switch } from 'src/components/atoms';
import { sanitizeQuillHtml } from 'src/components/atoms/ReadOnlyRichText/sanitizeQuillHtml';
import { stripHtml } from 'src/utils/stripHtml';
import { FormManagementLanguageChips } from '../FormManagementLanguageChips';
import { normalizeDescriptionForQuill } from '../utils/normalizeDescriptionForQuill';
import {
  FormInfoCard,
  FormInfoCardInner,
  FormInfoConfigurationsHeading,
  FormInfoContentRow,
  FormInfoDescription,
  FormInfoInnerWhiteCard,
  FormInfoIntakeChannelRow,
  FormInfoIntakeChannelsList,
  FormInfoIntakeChannelToggleLabel,
  FormInfoLanguagePills,
  FormInfoLeftColumn,
  FormInfoListItem,
  FormInfoListItemDivider,
  FormInfoListItemLabel,
  FormInfoListItemValue,
  FormInfoSection,
  FormInfoShowMore,
} from './FormDetailPage.styles';
import { FormDetailMetadata } from './types';

interface Props {
  metadata: FormDetailMetadata;
  'data-testid'?: string;
}

export const FormInformationSection = ({
  metadata,
  'data-testid': dataTestId = 'form-detail-info',
}: Props) => {
  const intakeChannelsReady =
    (metadata.intakeChannelsStatus ?? 'ready') === 'ready';
  const isSpeakUp = metadata.formType === 'SPEAK_UP';
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [needsDescriptionToggle, setNeedsDescriptionToggle] = useState(false);
  const descriptionClampRef = useRef<HTMLDivElement>(null);

  const { descriptionHtml, plainDescriptionContent } = (() => {
    const raw = metadata.description;

    const normalized = normalizeDescriptionForQuill(raw);
    return {
      descriptionHtml: sanitizeQuillHtml(normalized),
      plainDescriptionContent: stripHtml(normalized).trim(),
    };
  })();

  useEffect(() => {
    if (isDescriptionExpanded || !plainDescriptionContent) {
      setNeedsDescriptionToggle(false);
      return;
    }
    const el = descriptionClampRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      const overflow = el.scrollHeight > el.clientHeight + 1;
      setNeedsDescriptionToggle(overflow);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [descriptionHtml, plainDescriptionContent, isDescriptionExpanded]);

  return (
    <FormInfoSection data-testid={dataTestId}>
      <FormInfoCard data-testid={`${dataTestId}-card`}>
        <FormInfoConfigurationsHeading>
          Configurations
        </FormInfoConfigurationsHeading>
        <FormInfoContentRow>
          <FormInfoInnerWhiteCard>
            <FormInfoCardInner>
              <FormInfoLeftColumn>
                <FormInfoListItem data-testid={`${dataTestId}-category-row`}>
                  <FormInfoListItemLabel>Category</FormInfoListItemLabel>
                  <FormInfoListItemValue>
                    {metadata.category}
                  </FormInfoListItemValue>
                  <FormInfoListItemDivider />
                </FormInfoListItem>

                {plainDescriptionContent && (
                  <FormInfoListItem
                    data-testid={`${dataTestId}-description-row`}
                  >
                    <FormInfoListItemLabel>Description</FormInfoListItemLabel>
                    <FormInfoListItemValue>
                      {!isDescriptionExpanded && (
                        <FormInfoDescription
                          ref={descriptionClampRef}
                          $truncated
                          $isEmpty={false}
                          dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                          data-testid={`${dataTestId}-description-richtext-clamped`}
                        />
                      )}
                      {isDescriptionExpanded && (
                        <FormInfoDescription
                          $isEmpty={false}
                          dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                          data-testid={`${dataTestId}-description-richtext`}
                        />
                      )}
                      {needsDescriptionToggle && !isDescriptionExpanded && (
                        <FormInfoShowMore
                          onClick={() => setIsDescriptionExpanded(true)}
                          data-testid={`${dataTestId}-description-show-more`}
                        >
                          Show more
                        </FormInfoShowMore>
                      )}
                      {isDescriptionExpanded && (
                        <FormInfoShowMore
                          onClick={() => setIsDescriptionExpanded(false)}
                          data-testid={`${dataTestId}-description-show-less`}
                        >
                          Show less
                        </FormInfoShowMore>
                      )}
                    </FormInfoListItemValue>
                    <FormInfoListItemDivider />
                  </FormInfoListItem>
                )}

                {isSpeakUp && (
                  <FormInfoListItem data-testid={`${dataTestId}-type-row`}>
                    <FormInfoListItemLabel>Type</FormInfoListItemLabel>
                    <FormInfoListItemValue>
                      <FormInfoIntakeChannelsList>
                        <FormInfoIntakeChannelRow as="label">
                          <Switch
                            checked={metadata.isDeterminedCategory ?? false}
                            disabled
                            onClick={() => {}}
                            data-testid={`${dataTestId}-determined-category-toggle`}
                          />
                          <FormInfoIntakeChannelToggleLabel
                            $enabled={metadata.isDeterminedCategory ?? false}
                          >
                            Determined category
                          </FormInfoIntakeChannelToggleLabel>
                        </FormInfoIntakeChannelRow>
                        <FormInfoIntakeChannelRow as="label">
                          <Switch
                            checked={metadata.isReportedCategory ?? false}
                            disabled
                            onClick={() => {}}
                            data-testid={`${dataTestId}-reported-category-toggle`}
                          />
                          <FormInfoIntakeChannelToggleLabel
                            $enabled={metadata.isReportedCategory ?? false}
                          >
                            Reported category
                          </FormInfoIntakeChannelToggleLabel>
                        </FormInfoIntakeChannelRow>
                      </FormInfoIntakeChannelsList>
                    </FormInfoListItemValue>
                    <FormInfoListItemDivider />
                  </FormInfoListItem>
                )}

                {isSpeakUp && (
                  <FormInfoListItem
                    data-testid={`${dataTestId}-intake-channels-row`}
                  >
                    <FormInfoListItemLabel>
                      Intake channels
                    </FormInfoListItemLabel>
                    <FormInfoListItemValue>
                      {intakeChannelsReady ? (
                        <FormInfoIntakeChannelsList>
                          <FormInfoIntakeChannelRow as="label">
                            <Switch
                              checked={metadata.displayInApp ?? false}
                              disabled
                              onClick={() => {}}
                              data-testid={`${dataTestId}-reporting-portal-toggle`}
                            />
                            <FormInfoIntakeChannelToggleLabel
                              $enabled={metadata.displayInApp ?? false}
                            >
                              Vault
                            </FormInfoIntakeChannelToggleLabel>
                          </FormInfoIntakeChannelRow>
                          <FormInfoIntakeChannelRow as="label" $nested>
                            <Switch
                              checked={metadata.allowGoTogether ?? false}
                              disabled
                              onClick={() => {}}
                              data-testid={`${dataTestId}-go-together-toggle`}
                            />
                            <FormInfoIntakeChannelToggleLabel
                              $enabled={metadata.allowGoTogether ?? false}
                            >
                              GoTogether
                            </FormInfoIntakeChannelToggleLabel>
                          </FormInfoIntakeChannelRow>
                        </FormInfoIntakeChannelsList>
                      ) : (
                        <span>—</span>
                      )}
                    </FormInfoListItemValue>
                    <FormInfoListItemDivider />
                  </FormInfoListItem>
                )}

                {!isSpeakUp && (
                  <FormInfoListItem
                    data-testid={`${dataTestId}-auto-review-row`}
                  >
                    <FormInfoListItemLabel>Auto review</FormInfoListItemLabel>
                    <FormInfoListItemValue>
                      <FormInfoIntakeChannelRow as="label">
                        <Switch
                          checked={metadata.autoReview ?? false}
                          disabled
                          onClick={() => {}}
                          data-testid={`${dataTestId}-auto-review-toggle`}
                        />
                        <FormInfoIntakeChannelToggleLabel
                          $enabled={metadata.autoReview ?? false}
                        >
                          Auto review
                        </FormInfoIntakeChannelToggleLabel>
                      </FormInfoIntakeChannelRow>
                    </FormInfoListItemValue>
                    <FormInfoListItemDivider />
                  </FormInfoListItem>
                )}

                {!isSpeakUp && (
                  <FormInfoListItem
                    data-testid={`${dataTestId}-intake-channels-row`}
                  >
                    <FormInfoListItemLabel>
                      Intake channels
                    </FormInfoListItemLabel>
                    <FormInfoListItemValue>
                      <FormInfoIntakeChannelRow as="label">
                        <Switch
                          checked={metadata.displayInApp ?? false}
                          disabled
                          onClick={() => {}}
                          data-testid={`${dataTestId}-reporting-portal-toggle`}
                        />
                        <FormInfoIntakeChannelToggleLabel
                          $enabled={metadata.displayInApp ?? false}
                        >
                          Vault
                        </FormInfoIntakeChannelToggleLabel>
                      </FormInfoIntakeChannelRow>
                    </FormInfoListItemValue>
                    <FormInfoListItemDivider />
                  </FormInfoListItem>
                )}

                {!isSpeakUp && (
                  <FormInfoListItem data-testid={`${dataTestId}-country-row`}>
                    <FormInfoListItemLabel>Country</FormInfoListItemLabel>
                    <FormInfoListItemValue>
                      <FormInfoLanguagePills>
                        <FormManagementLanguageChips
                          languages={metadata.countries ?? []}
                          maxVisible={Infinity}
                          data-testid={`${dataTestId}-country-chips`}
                        />
                      </FormInfoLanguagePills>
                    </FormInfoListItemValue>
                    <FormInfoListItemDivider />
                  </FormInfoListItem>
                )}

                <FormInfoListItem data-testid={`${dataTestId}-languages-row`}>
                  <FormInfoListItemLabel>Languages</FormInfoListItemLabel>
                  <FormInfoListItemValue>
                    <FormInfoLanguagePills>
                      <FormManagementLanguageChips
                        languages={metadata.languageVariants}
                        maxVisible={Infinity}
                        data-testid={`${dataTestId}-language-chips`}
                      />
                    </FormInfoLanguagePills>
                  </FormInfoListItemValue>
                  <FormInfoListItemDivider />
                </FormInfoListItem>

                <FormInfoListItem data-testid={`${dataTestId}-created-by-row`}>
                  <FormInfoListItemLabel>Created by</FormInfoListItemLabel>
                  <FormInfoListItemValue>
                    {metadata.createdBy}
                  </FormInfoListItemValue>
                  <FormInfoListItemDivider />
                </FormInfoListItem>

                <FormInfoListItem data-testid={`${dataTestId}-created-at-row`}>
                  <FormInfoListItemLabel>Created at</FormInfoListItemLabel>
                  <FormInfoListItemValue>
                    {metadata.createdDate}
                  </FormInfoListItemValue>
                  <FormInfoListItemDivider />
                </FormInfoListItem>

                <FormInfoListItem
                  data-testid={`${dataTestId}-last-updated-row`}
                >
                  <FormInfoListItemLabel>Last updated</FormInfoListItemLabel>
                  <FormInfoListItemValue>
                    {metadata.lastUpdatedDate}
                  </FormInfoListItemValue>
                </FormInfoListItem>
              </FormInfoLeftColumn>
            </FormInfoCardInner>
          </FormInfoInnerWhiteCard>
        </FormInfoContentRow>
      </FormInfoCard>
    </FormInfoSection>
  );
};
